import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X, ImageIcon, Phone, Mail, MapPin } from "lucide-react";
import { services } from "@/components/Services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_PHOTOS = 6;
const MAX_BYTES = 5 * 1024 * 1024;

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        suburb: "",
        service: services[0].title,
        details: "",
    });
    const [photos, setPhotos] = useState([]); // array of File
    const [previews, setPreviews] = useState([]); // array of objectURL
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const onChange = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const onPickPhotos = (e) => {
        const list = Array.from(e.target.files || []);
        if (!list.length) return;
        const slots = MAX_PHOTOS - photos.length;
        const accepted = [];
        const rejected = [];
        for (const f of list.slice(0, slots)) {
            if (!f.type.startsWith("image/") || f.size > MAX_BYTES) {
                rejected.push(f.name);
                continue;
            }
            accepted.push(f);
        }
        if (rejected.length) {
            toast.error(`Skipped: ${rejected.join(", ")} (too big or not an image)`);
        }
        const next = [...photos, ...accepted];
        const nextPrev = next.map((f) => URL.createObjectURL(f));
        // Revoke previous previews not in next
        previews.forEach((u) => URL.revokeObjectURL(u));
        setPhotos(next);
        setPreviews(nextPrev);
        if (fileRef.current) fileRef.current.value = "";
    };

    const removePhoto = (idx) => {
        URL.revokeObjectURL(previews[idx]);
        setPhotos(photos.filter((_, i) => i !== idx));
        setPreviews(previews.filter((_, i) => i !== idx));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        const required = ["name", "phone", "email", "suburb", "service", "details"];
        for (const k of required) {
            if (!String(form[k] || "").trim()) {
                toast.error("Please fill in all the fields so we can quote properly.");
                return;
            }
        }
        setBusy(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            photos.forEach((f) => fd.append("photos", f, f.name));
            const res = await axios.post(`${API}/quote`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // res not used in success copy; keep reference to avoid lint
            void res;
            toast.success(
                "Thanks, your quote request has been sent. FreshEdge Services will get back to you soon."
            );
            setForm({
                name: "",
                phone: "",
                email: "",
                suburb: "",
                service: services[0].title,
                details: "",
            });
            previews.forEach((u) => URL.revokeObjectURL(u));
            setPhotos([]);
            setPreviews([]);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg =
                typeof detail === "string"
                    ? detail
                    : Array.isArray(detail) && detail[0]?.msg
                    ? detail[0].msg
                    : "Couldn’t send right now. Please try again or call us.";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="section-contact"
            className="relative bg-[var(--paper-2)] py-28 md:py-40 border-t border-line"
        >
            <div className="maxw section-pad">
                <div className="grid lg:grid-cols-12 gap-14">
                    <div className="lg:col-span-5">
                        <div className="eyebrow mb-5">Get a free quote</div>
                        <h2 className="font-display font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                            Send a few{" "}
                            <span className="italic text-[var(--terra)]">
                                details
                            </span>{" "}
                            — we’ll do the rest.
                        </h2>
                        <p className="mt-6 text-[rgba(14,14,16,0.72)] text-base leading-relaxed max-w-md">
                            Tell us what needs doing, where, and (if it helps)
                            attach a few photos. We reply within a day with a
                            clear price and a time that works.
                        </p>

                        <div className="mt-10 grid grid-cols-1 gap-3 max-w-md">
                            <a
                                href="tel:0435995971"
                                className="flex items-center gap-4 px-5 py-4 rounded-xl border border-line bg-[var(--paper)] hover:border-[var(--terra)] transition"
                                data-testid="contact-phone-link"
                            >
                                <Phone size={16} className="text-[var(--terra)]" />
                                <div className="flex-1">
                                    <div className="eyebrow text-[10px]">Phone</div>
                                    <div className="font-display text-lg tracking-tight">
                                        0435 995 971
                                    </div>
                                </div>
                                <span className="font-mono-x text-[11px] text-[rgba(14,14,16,0.48)]">
                                    →
                                </span>
                            </a>
                            <a
                                href="mailto:Thefreshedgeservice@gmail.com"
                                className="flex items-center gap-4 px-5 py-4 rounded-xl border border-line bg-[var(--paper)] hover:border-[var(--terra)] transition"
                                data-testid="contact-email-link"
                            >
                                <Mail size={16} className="text-[var(--terra)]" />
                                <div className="flex-1">
                                    <div className="eyebrow text-[10px]">Email</div>
                                    <div className="font-display text-[15px] tracking-tight break-all">
                                        Thefreshedgeservice@gmail.com
                                    </div>
                                </div>
                                <span className="font-mono-x text-[11px] text-[rgba(14,14,16,0.48)]">
                                    →
                                </span>
                            </a>
                            <div className="flex items-center gap-4 px-5 py-4 rounded-xl border border-line bg-[var(--paper)]">
                                <MapPin size={16} className="text-[var(--terra)]" />
                                <div className="flex-1">
                                    <div className="eyebrow text-[10px]">
                                        Service area
                                    </div>
                                    <div className="font-display text-[15px] tracking-tight">
                                        Queanbeyan · Canberra · Tuggeranong
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <form
                        onSubmit={submit}
                        data-testid="contact-form"
                        encType="multipart/form-data"
                        className="lg:col-span-7 bg-[var(--paper)] border border-line rounded-2xl p-7 md:p-10 flex flex-col gap-6"
                    >
                        <div className="flex items-center justify-between">
                            <div className="font-mono-x text-[11px] uppercase tracking-widest text-[rgba(14,14,16,0.58)]">
                                Free quote · No obligation
                            </div>
                            <div className="font-mono-x text-[11px] text-[var(--terra)]">
                                Replies usually under 5h
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-6">
                            <Field label="Your name" htmlFor="name">
                                <input
                                    id="name"
                                    data-testid="contact-name"
                                    value={form.name}
                                    onChange={onChange("name")}
                                    placeholder="Alex Walker"
                                    className="input"
                                    required
                                    maxLength={120}
                                />
                            </Field>
                            <Field label="Phone" htmlFor="phone">
                                <input
                                    id="phone"
                                    data-testid="contact-phone"
                                    value={form.phone}
                                    onChange={onChange("phone")}
                                    placeholder="(000) 000 0000"
                                    className="input"
                                    required
                                    inputMode="tel"
                                    maxLength={40}
                                />
                            </Field>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-6">
                            <Field label="Email" htmlFor="email">
                                <input
                                    id="email"
                                    data-testid="contact-email"
                                    type="email"
                                    value={form.email}
                                    onChange={onChange("email")}
                                    placeholder="alex@email.com"
                                    className="input"
                                    required
                                />
                            </Field>
                            <Field label="Suburb" htmlFor="suburb">
                                <input
                                    id="suburb"
                                    data-testid="contact-suburb"
                                    value={form.suburb}
                                    onChange={onChange("suburb")}
                                    placeholder="Where the job is"
                                    className="input"
                                    required
                                    maxLength={120}
                                />
                            </Field>
                        </div>

                        <Field label="Service needed" htmlFor="service">
                            <div className="relative">
                                <select
                                    id="service"
                                    data-testid="contact-service"
                                    value={form.service}
                                    onChange={onChange("service")}
                                    className="input pr-10 appearance-none"
                                    required
                                >
                                    {services.map((s) => (
                                        <option key={s.slug} value={s.title}>
                                            {s.title}
                                        </option>
                                    ))}
                                    <option value="Not sure / mixed">
                                        Not sure / mixed
                                    </option>
                                </select>
                                <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[rgba(14,14,16,0.48)] text-sm">
                                    ▾
                                </span>
                            </div>
                        </Field>

                        <Field label="Job details" htmlFor="details">
                            <textarea
                                id="details"
                                data-testid="contact-details"
                                rows={5}
                                value={form.details}
                                onChange={onChange("details")}
                                placeholder="Tell us what needs doing — size of the area, current state, anything tricky."
                                className="input resize-y min-h-[140px]"
                                required
                                maxLength={2000}
                            />
                        </Field>

                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <span className="eyebrow text-[10px]">
                                    Photos (optional)
                                </span>
                                <span className="font-mono-x text-[10.5px] text-[rgba(14,14,16,0.48)]">
                                    {photos.length}/{MAX_PHOTOS} · max 5MB each
                                </span>
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                                {previews.map((src, i) => (
                                    <div
                                        key={i}
                                        className="relative aspect-square rounded-lg overflow-hidden border border-line group"
                                        data-testid={`preview-${i}`}
                                    >
                                        <img
                                            src={src}
                                            alt={`upload-${i}`}
                                            className="w-full h-full object-cover"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removePhoto(i)}
                                            data-testid={`remove-photo-${i}`}
                                            className="absolute inset-0 bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                            aria-label="Remove photo"
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                ))}
                                {photos.length < MAX_PHOTOS && (
                                    <label
                                        htmlFor="photos"
                                        className="aspect-square rounded-lg border border-dashed border-line-strong bg-[var(--paper-2)]/40 hover:bg-[var(--paper-2)] hover:border-[var(--terra)] transition cursor-pointer flex flex-col items-center justify-center gap-1 text-[rgba(14,14,16,0.58)]"
                                        data-testid="photo-picker"
                                    >
                                        <Upload size={18} />
                                        <span className="font-mono-x text-[10px]">
                                            Add
                                        </span>
                                    </label>
                                )}
                            </div>
                            <input
                                ref={fileRef}
                                id="photos"
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={onPickPhotos}
                                className="hidden"
                                data-testid="contact-photos"
                            />
                            <p className="mt-3 flex items-center gap-2 text-[12px] text-[rgba(14,14,16,0.58)]">
                                <ImageIcon size={12} />
                                A few photos help us quote accurately — front,
                                back, and any tricky spots.
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                            <div className="font-mono-x text-[11px] text-[rgba(14,14,16,0.48)] max-w-xs">
                                By sending this you agree we’ll reach out about
                                your job. No spam, no list.
                            </div>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="contact-submit"
                                className="btn-ink disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {busy ? "Sending…" : "Send quote request"}
                                <span>→</span>
                            </button>
                        </div>

                        <style>{`
              .input {
                width: 100%;
                background: var(--paper-2);
                border: 1px solid var(--line);
                border-radius: 12px;
                padding: 14px 16px;
                font: 500 14.5px/1.45 "Manrope", sans-serif;
                color: var(--ink);
                transition: border-color .2s ease, background .2s ease;
              }
              .input::placeholder { color: rgba(14,14,16,.35); }
              .input:focus { outline: none; border-color: var(--ink); background: var(--paper); }
            `}</style>
                    </form>
                </div>
            </div>
        </section>
    );
}

function Field({ label, htmlFor, children }) {
    return (
        <label htmlFor={htmlFor} className="flex flex-col gap-2">
            <span className="eyebrow text-[10px]">{label}</span>
            {children}
        </label>
    );
}
